import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/styles/index.css';
import AdminLogin from '@/pages/AdminLogin';
import Dashboard from '@/pages/Dashboard';
import Posts from '@/pages/Posts';
import Prints from '@/pages/Prints';
import Subscribers from '@/pages/Subscribers';
import Settings from '@/pages/Settings';
import Supporters from '@/pages/Supporters';
import Subscribe from '@/pages/Subscribe';
import EmailPreferences from '@/pages/EmailPreferences';
import CommentRepliesStop from '@/pages/CommentRepliesStop';
import ListChoice from '@/pages/ListChoice';
import Support from '@/pages/Support';
import SupporterSuccess from '@/pages/SupporterSuccess';
import PublicHome from '@/pages/PublicHome';
import PostView from '@/pages/PostView';
import AddToHomeScreen from '@/pages/AddToHomeScreen';
import Gallery from '@/pages/Gallery';
import OrderSuccess from '@/pages/OrderSuccess';
import Muggs from '@/pages/Muggs';
import MuggsSuccess from '@/pages/MuggsSuccess';
import Notecards from '@/pages/Notecards';
import NotecardsSuccess from '@/pages/NotecardsSuccess';
import Tees from '@/pages/Tees';
import TeesCategory from '@/pages/TeesCategory';
import TeesGender from '@/pages/TeesGender';
import TeesSale from '@/pages/TeesSale';
import TeesProduct from '@/pages/TeesProduct';
import TeesSuccess from '@/pages/TeesSuccess';
import SubscribeSuccess from '@/pages/SubscribeSuccess';
import AdminMuggs from '@/pages/AdminMuggs';
import AdminOrders from '@/pages/AdminOrders';
import OrderPackingSlip from '@/pages/OrderPackingSlip';
import AdminComments from '@/pages/AdminComments';
import AdminNotecards from '@/pages/AdminNotecards';
import AdminTees from '@/pages/AdminTees';
import AdminDaily from '@/pages/AdminDaily';
import DailyOBXComingSoon from '@/pages/DailyOBXComingSoon';
import DailyOBXSuccess from '@/pages/DailyOBXSuccess';
import MrsBum from '@/pages/MrsBum';
import MrsBumCategory from '@/pages/MrsBumCategory';
import MrsBumProduct from '@/pages/MrsBumProduct';
import MrsBumSuccess from '@/pages/MrsBumSuccess';
import AdminMrsBum from '@/pages/AdminMrsBum';
import Wicks from '@/pages/Wicks';
import WicksSuccess from '@/pages/WicksSuccess';
import AdminWicks from '@/pages/AdminWicks';
import Calendar from '@/pages/Calendar';
import AdminCalendar from '@/pages/AdminCalendar';
import Broadcast from '@/pages/Broadcast';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Shipping from '@/pages/Shipping';
import FAQ from '@/pages/FAQ';
import About from '@/pages/About';
import Archive from '@/pages/Archive';
import CartSuccess from '@/pages/CartSuccess';
import { CartProvider } from '@/context/CartContext';
import CartDrawer from '@/components/CartDrawer';
import { Toaster } from 'sonner';
import { SignupRouteTracker } from '@/utils/signupTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set axios defaults
axios.defaults.baseURL = API;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      await axios.get('/admin/verify');
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('admin_token');
      delete axios.defaults.headers.common['Authorization'];
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token) => {
    localStorage.setItem('admin_token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <CartProvider>
    <div className="App">
      <Toaster position="top-right" richColors />
      <CartDrawer />
      <BrowserRouter>
        <SignupRouteTracker />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<PublicHome />} />
          <Route path="/post/:slug" element={<PostView />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/unsubscribe" element={<EmailPreferences key="unsubscribe" mode="unsubscribe" />} />
          {/* Emails sent before Oct 2026 linked here; nginx serves the app for it. The keys make
              React start the page fresh when its own links switch between these routes. */}
          <Route path="/api/unsubscribe" element={<EmailPreferences key="unsubscribe" mode="unsubscribe" />} />
          <Route path="/email-preferences" element={<EmailPreferences key="preferences" mode="preferences" />} />
          <Route path="/comment-replies/stop" element={<CommentRepliesStop />} />
          <Route path="/list/keep" element={<ListChoice choice="keep" />} />
          <Route path="/list/remove" element={<ListChoice choice="remove" />} />
          <Route path="/support" element={<Support />} />
          <Route path="/supporter-success" element={<SupporterSuccess />} />
          <Route path="/subscribe-success" element={<SubscribeSuccess />} />
          <Route path="/add-to-homescreen" element={<AddToHomeScreen />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/order-success" element={<OrderSuccess />} />
          <Route path="/shop/muggs" element={<Muggs />} />
          <Route path="/muggs-success" element={<MuggsSuccess />} />
          <Route path="/shop/tees" element={<Tees />} />
          <Route path="/shop/tees/men" element={<TeesGender gender="men" />} />
          <Route path="/shop/tees/women" element={<TeesGender gender="women" />} />
          <Route path="/shop/tees/kids" element={<TeesGender gender="kids" />} />
          <Route path="/shop/tees/sale" element={<TeesSale />} />
          <Route path="/shop/tees/:category" element={<TeesCategory />} />
          <Route path="/shop/tees/:category/:productId" element={<TeesProduct />} />
          <Route path="/tees-success" element={<TeesSuccess />} />
          <Route path="/shop/notecards" element={<Notecards />} />
          <Route path="/notecards-success" element={<NotecardsSuccess />} />
          <Route path="/daily" element={<DailyOBXComingSoon />} />
          <Route path="/shop/mrsbum" element={<MrsBum />} />
          <Route path="/shop/mrsbum/:category" element={<MrsBumCategory />} />
          <Route path="/shop/mrsbum/:category/:productId" element={<MrsBumProduct />} />
          <Route path="/mrsbum-success" element={<MrsBumSuccess />} />
          <Route path="/shop/wicks" element={<Wicks />} />
          <Route path="/wicks-success" element={<WicksSuccess />} />
          <Route path="/shop/calendar" element={<Calendar />} />
          <Route path="/shop/calendar/:slug" element={<Calendar />} />
          <Route path="/daily-obx-success" element={<DailyOBXSuccess />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/shipping" element={<Shipping />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/about" element={<About />} />
          <Route path="/archive" element={<Archive />} />
          <Route path="/cart-success" element={<CartSuccess />} />

          {/* Admin routes */}
          <Route
            path="/admin/login"
            element={
              isAuthenticated ? (
                <Navigate to="/admin/dashboard" replace />
              ) : (
                <AdminLogin onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              isAuthenticated ? (
                <Dashboard onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/posts"
            element={
              isAuthenticated ? (
                <Posts onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/prints"
            element={
              isAuthenticated ? (
                <Prints onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/orders"
            element={
              isAuthenticated ? (
                <AdminOrders onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/orders/:shop/:id/slip"
            element={
              isAuthenticated ? (
                <OrderPackingSlip />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/comments"
            element={
              isAuthenticated ? (
                <AdminComments onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/muggs"
            element={
              isAuthenticated ? (
                <AdminMuggs onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/notecards"
            element={
              isAuthenticated ? (
                <AdminNotecards onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/subscribers"
            element={
              isAuthenticated ? (
                <Subscribers onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/supporters"
            element={
              isAuthenticated ? (
                <Supporters onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/settings"
            element={
              isAuthenticated ? (
                <Settings onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/daily"
            element={
              isAuthenticated ? (
                <AdminDaily onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/tees"
            element={
              isAuthenticated ? (
                <AdminTees onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/mrsbum"
            element={
              isAuthenticated ? (
                <AdminMrsBum onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/wicks"
            element={
              isAuthenticated ? (
                <AdminWicks onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/calendar"
            element={
              isAuthenticated ? (
                <AdminCalendar onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/broadcast"
            element={
              isAuthenticated ? (
                <Broadcast onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />

          {/* Default redirect */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
    </CartProvider>
  );
}

export default App;